import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getStorageItem, setStorageItem } from 'lib/utils/storageUtils'

/**
 * Shared expand/collapse state for navigation groups (both the titled
 * sections and the individual menu items that have sub-links). The expanded
 * state is persisted per-`storageKey` in localStorage and auto-expands when
 * the current route matches `expandOnMatchingPath` so the active item is never
 * hidden behind a collapsed group on navigation.
 */
export const useNavExpansionState = ({
  storageKey,
  defaultIsExpanded = true,
  expandOnMatchingPath
}: {
  storageKey: string
  defaultIsExpanded?: boolean
  expandOnMatchingPath?: (pathname: string) => boolean
}) => {
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(() =>
    getStorageItem(storageKey, defaultIsExpanded)
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: check location.pathname only
  useEffect(() => {
    if (!expandOnMatchingPath?.(location.pathname) || isExpanded) {
      return
    }

    setIsExpanded(true)
  }, [location.pathname])

  const toggle = () =>
    setIsExpanded(expanded => {
      const newState = !expanded
      setStorageItem(storageKey, newState)
      return newState
    })

  return { isExpanded, toggle }
}
